import { z } from 'zod';

const TextNode = z
  .object({
    text: z.string(),
  })
  .strict()
  .transform(({ text }) => ({
    text: text.endsWith('\r') ? text.slice(0, text.length - 1) : text,
    type: 'text' as const,
  }));

const Hyperlink = z.object({
  type: z.literal('hyperlink'),
  children: z.array(TextNode),
});

const Addition = z.object({
  type: z.literal('addition'),
  children: z.array(z.union([TextNode, Hyperlink])),
});

const Removal = z.object({
  type: z.literal('removal'),
  children: z.array(z.union([TextNode, Hyperlink])),
});

const Suggestion = z.object({
  type: z.literal('suggestion'),
  children: z.array(z.union([Addition, Removal])).length(1),
});

const Heading = z.object({
  type: z.literal('heading'),
  level: z.number().min(1).max(2),
  children: z.array(z.union([TextNode, Hyperlink, Suggestion])),
});

const Paragraph = z.object({
  type: z.literal('paragraph'),
  children: z.array(z.union([TextNode, Hyperlink, Suggestion])),
});

const Omitted = z.object({
  type: z.literal('omitted'),
});

export type DocumentPreviewNode = z.infer<typeof DocumentPreviewNode>;
const DocumentPreviewNode = z.union([
  Paragraph,
  Heading,
  Suggestion,
  Removal,
  Addition,
  Hyperlink,
  TextNode,
  Omitted,
]);

const Point = z.object({
  path: z.array(z.number()),
  offset: z.number(),
});

const Range = z.object({
  start: Point,
  end: Point,
});

export type DocumentPreviewComment = z.infer<typeof DocumentPreviewComment>;
const DocumentPreviewComment = z.object({
  author: z.string(),
  text: z.string(),
  range: Range,
});

const DocumentPreview = z.object({
  completed: z.boolean(),
  document: z.object({
    children: z.array(z.union([Heading, Paragraph, Omitted])),
    comments: z.array(DocumentPreviewComment).default([]),
  }),
  recommendedSuggestions: z.number(),
});

export type DocumentPreview = z.infer<typeof DocumentPreview>;

/**
 * Parses `unknown` data into a `DocumentPreview`.
 * Throws on error.
 */
export function parseDocumentPreview(data: unknown): DocumentPreview {
  return DocumentPreview.parse(data);
}
